import React, { useContext, useState, useEffect } from "react"
import { WingsModal } from "@wingspans-ui"
import { FirebaseContext } from "../Firebase"
import ExperienceInput from "./experience-input"
import { ExperienceEditorContext } from "./ExperienceEditorContext"
import { checkRequiredFields } from "./utils/CheckRequiredFields"
import { FormattedMessage } from "react-intl"
import { sendStandardToast } from "../toasts/standard-toast"
import SkillsInterestsEditor from "./SkillsInterestsEditor"
import { getExperienceAbbreviatedLabel, getExperienceInstructions, experienceTypes } from "./utils/experienceTypes"

const ResumeExperienceEditorModal = ({ experienceData, isNewExperience, show, closeTimelineModal, type }) => {
  const { id } = experienceData || {}
  const { firebase, notifier, profile } = useContext(FirebaseContext)

  /**
   *
   * State for all form data across every type of resume experience
   *
   */
  const [experience, setExperience] = useState(isNewExperience ? {} : { ...experienceData, type: type })
  const [isOptimizerModalOpen, setIsOptimizerModalOpen] = useState(false)

  // use the ID from experienceData to update the experience state on initial render
  useEffect(() => {
    let updatedExperienceData = {}
    if (experienceData) {
      Object.assign(updatedExperienceData, experienceData)
    }
    if (isNewExperience && type) {
      updatedExperienceData.type = type
    }
    setExperience(experience => ({...experience, ...updatedExperienceData}))
  }, [id, type])

  if (!experienceTypes.includes(type)) {
    return null
  }

  const setLocalExperienceKeyValue = (key, value) => {
    setExperience({
      ...experience,
      [key]: value,
    })
  }

  const dispatchHighlightAction = (type, payload, index) => {
    let highlightsCopy = experience.highlights ? [...experience.highlights] : []

    switch (type) {
      case "INITIALIZE_EMPTY_FIELDS":
        if (highlightsCopy.length === 0) {
          highlightsCopy = Array(3).fill("")
        }
        break
      case "UPDATE_INPUT_VALUE":
        highlightsCopy[index] = payload
        break
      case "DELETE_INPUT_FIELD":
        highlightsCopy.splice(index, 1)
        break
      case "CREATE_INPUT_FIELD":
        highlightsCopy.push("")
        break
      default:
        break
    }
    setExperience((oldData) => ({
      ...oldData,
      highlights: highlightsCopy,
    }))
  }

  const toggleExperienceIsCurrent = (status) => {
    if (status) {
      setExperience({
        ...experience,
        endDateMonth: null,
        endDateYear: null,
      })
    } else {
      setExperience({
        ...experience,
        endDateMonth: "",
        endDateYear: "",
      })
    }
  }

  const toggleExperienceOnlyHasEndDate = (status) => {
    if (status) {
      setExperience({
        ...experience,
        startDateMonth: null,
        startDateYear: null,
      })
    } else {
      setExperience({
        ...experience,
        startDateMonth: "",
        startDateYear: "",
      })
    }
  }

  const handleCreateResumeExperience = async (experienceData) => {
    const errors = checkRequiredFields(experience, type)
    if (errors) {
      sendStandardToast({
        message: (
          <>
            {errors.map((error) => (
              <div>{error}</div>
            ))}
          </>
        ),
        heading: <FormattedMessage id="resume-experience-editor-modal-submission-required-foll" defaultMessage="Submission requires the following" />,
      })
      return
    }

    try {
      await firebase.createResumeExperience(experienceData)
    } catch (e) {
      console.error(e)
    }
    handleCloseModal()
    await notifier.handleNewResumeExperience(profile)
  }

  const handleDeleteResumeExperience = async (experienceData) => {
    try {
      await firebase.deleteResumeExperience(experienceData.id)
    } catch (e) {
      console.error(e)
    }
    handleCloseModal()
  }

  const handleUpdateResumeExperience = async (experienceData) => {
    const errors = checkRequiredFields(experience, type)
    if (errors) {
      sendStandardToast({
        message: (
          <>
            {errors.map((error) => (
              <div>{error}</div>
            ))}
          </>
        ),
        heading: <FormattedMessage id="resume-experience-editor-modal-submission-required-foll" defaultMessage="Submission requires the following" />,
      })
      return
    }
    try {
      await firebase.editResumeExperience(experienceData)
      // update the experience state with the new experience data so that the modal reflects the changes after submission and reopening
      setExperience({ ...experienceData, type: type })
    } catch (e) {
      console.error(e)
    }
    handleCloseModal(true)
  }

  /**
   * If a closeTimelineModal callback function was provided, call it. Reset form
   * values to either:
   * - empty (if this is a new experience)
   * - the saved values (if this is an existing experience)
   * 
   * @param {boolean} preserveFormValues - If true, don't reset the form values.
   * This currently only happens when updating an experience
   */
  const handleCloseModal = (preserveFormValues) => {
    if (closeTimelineModal) {
      closeTimelineModal()
    }

    if (!preserveFormValues) {
      setExperience(isNewExperience ? {type: type} : {...experienceData})
    }
  }

  return (
    <ExperienceEditorContext.Provider
      value={{
        experience,
        setLocalExperienceKeyValue,
        dispatchHighlightAction,
        checkRequiredFields,
        toggleExperienceIsCurrent,
        toggleExperienceOnlyHasEndDate,
        isOptimizerModalOpen,
        setIsOptimizerModalOpen,
      }}
    >
      <WingsModal
        className="editor-modal"
        isOpen={show}
        onClose={() => handleCloseModal()}
        size="lg"
        headerAlign="center"
        titleEyebrow={(
          <FormattedMessage
            id={isNewExperience ? "resume-experience-editor-modal-add" : "resume-experience-editor-modal-edit"}
            defaultMessage={isNewExperience ? "Add" : "Edit"}
          />
        )}
        title={(
          <>
            {getExperienceAbbreviatedLabel(type)} <FormattedMessage id="resume-experience-editor-modal-experience" defaultMessage="Experience" />
          </>
        )}
        titleLevel={3}
        secondaryActionLabel={type !== "skills" && type !== "interests" && !isNewExperience ? (
          <FormattedMessage id="resume-experience-editor-modal-delete" defaultMessage="Delete" />
        ) : null}
        onSecondaryAction={type !== "skills" && type !== "interests" && !isNewExperience
          ? () => handleDeleteResumeExperience(experience)
          : undefined}
        primaryActionLabel={type !== "skills" && type !== "interests" ? (
          <FormattedMessage id="resume-experience-editor-modal-save" defaultMessage="Save" />
        ) : null}
        onPrimaryAction={type !== "skills" && type !== "interests"
          ? (isNewExperience ? () => handleCreateResumeExperience(experience) : () => handleUpdateResumeExperience(experience))
          : undefined}
      >
        <WingsModal.Body>
          {isNewExperience && <p style={{ fontFamily: "merriweather", fontSize: "16px" }}>{getExperienceInstructions(type)}</p>}
          {(type === "skills" || type === "interests") && <SkillsInterestsEditor currentItems={profile[type] || []} type={type} />}
          {type !== "skills" && type !== "interests" && (
            <div className="form career-builder-milestones-form">
              <ExperienceInput type={type} dispatchHighlightAction={dispatchHighlightAction} />
            </div>
          )}
        </WingsModal.Body>
      </WingsModal>
    </ExperienceEditorContext.Provider>
  )
}

export default ResumeExperienceEditorModal
